import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { createLogger } from '../utils/logger';
import { apiFetch } from '../utils/api';
import { fetchLiveJobs } from '../hooks/useJobs';

const log = createLogger('GeneratePage');

interface SubmissionResult {
  scan_id: string;
  job_id?: string;
  status: 'pending' | 'submitted' | 'error';
  error?: string;
}

type LicensePlateOption = 'no_plate' | 'bracket_only' | 'keep_plate';

const LP_OPTIONS: { value: LicensePlateOption; label: string }[] = [
  { value: 'no_plate', label: 'No plate' },
  { value: 'bracket_only', label: 'Bracket only' },
  { value: 'keep_plate', label: 'Keep plate' },
];

type GenerationTierOption = 'tier0' | 'tier1' | 'tier2' | 'tier3' | 'tier4';

// The resolution names what a tier renders at, not what it returns: every tier
// is delivered at 1600x1200, so a tier trades cost against detail only.
// Tier 0 is the exception: it swaps the engine for Meta Muse Image, which sizes
// its own output. It renders square stacked pairs against a fixed backdrop and
// swaps the chosen photobooth into every view once the pairs are split.
const GENERATION_TIER_OPTIONS: { value: GenerationTierOption; label: string }[] = [
  { value: 'tier0', label: 'Tier 0 — Muse Image, background replaced' },
  { value: 'tier1', label: 'Tier 1 — 1K render / medium' },
  { value: 'tier2', label: 'Tier 2 — 2K render / medium' },
  { value: 'tier3', label: 'Tier 3 — 1K render / high' },
  { value: 'tier4', label: 'Tier 4 — 2K render / high' },
];

async function findAcceptedLiveJob(
  scanId: string,
  generationTier: GenerationTierOption,
  submittedAt: number,
) {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    try {
      const jobs = await fetchLiveJobs();
      const acceptedJob = jobs
        .filter((job) => (
          job.scan_id === scanId
          && job.source === 'manual'
          && job.generation_tier === generationTier
          && Date.parse(job.created_at) >= submittedAt - 5_000
        ))
        .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];

      if (acceptedJob) return acceptedJob;
    } catch (error) {
      log.warn('Could not reconcile ambiguous scan submission:', error);
    }
  }

  return null;
}

// Pre-known pickup bed/cabin configuration ('' = not specified -> backend infers
// nothing and leaves it out of the prompt).
type BedTypeOption = '' | 'short' | 'standard' | 'long';
type CabinTypeOption =
  | ''
  | 'regular'
  | 'supercab'
  | 'supercrew'
  | 'double'
  | 'crew'
  | 'quad'
  | 'super'
  | 'mega';

const BED_OPTIONS: { value: BedTypeOption; label: string }[] = [
  { value: '', label: 'Not specified' },
  { value: 'short', label: 'Short bed' },
  { value: 'standard', label: 'Standard bed' },
  { value: 'long', label: 'Long bed' },
];

const CABIN_OPTIONS: { value: CabinTypeOption; label: string }[] = [
  { value: '', label: 'Not specified' },
  { value: 'regular', label: 'Regular' },
  { value: 'supercab', label: 'SuperCab' },
  { value: 'supercrew', label: 'SuperCrew' },
  { value: 'double', label: 'Double' },
  { value: 'crew', label: 'Crew' },
  { value: 'quad', label: 'Quad' },
  { value: 'super', label: 'Super' },
  { value: 'mega', label: 'Mega' },
];

// Batch CSV column parsing. Header names that identify the scan-id column and
// the optional per-scan pickup bed/cabin columns. Values are matched
// case-insensitively against the closed vocabulary above; unrecognized cells
// are ignored so one bad value never blocks the whole batch.
const SCAN_ID_HEADERS = ['scan_id', 'scanid', 'id', 'scan', 'inspection_id', 'inspectionid'];
const BED_COLUMN = 'pickup_bed';
const CABIN_COLUMN = 'pickup_cabin';
const BED_TOKENS = new Set<string>(BED_OPTIONS.map((o) => o.value).filter(Boolean));
const CABIN_TOKENS = new Set<string>(CABIN_OPTIONS.map((o) => o.value).filter(Boolean));

type BatchScanSettings = { bed_type?: string; cabin_type?: string };

export function GeneratePage() {
  // Single scan state
  const [singleScanId, setSingleScanId] = useState('');
  const [singleSubmitting, setSingleSubmitting] = useState(false);
  const [singleResult, setSingleResult] = useState<SubmissionResult | null>(null);

  // Single scan settings
  const [photoboothName, setPhotoboothName] = useState('');
  const [lpFront, setLpFront] = useState<LicensePlateOption>('no_plate');
  const [lpRear, setLpRear] = useState<LicensePlateOption>('bracket_only');
  const [highReflectance, setHighReflectance] = useState(false);
  const [metadataFree, setMetadataFree] = useState(false);
  const [generationTier, setGenerationTier] = useState<GenerationTierOption>('tier4');
  // Pre-known pickup bed/cabin (only sent when not 'Not specified')
  const [bedType, setBedType] = useState<BedTypeOption>('');
  const [cabinType, setCabinType] = useState<CabinTypeOption>('');

  // Batch upload state
  const [batchFileName, setBatchFileName] = useState<string | null>(null);
  const [batchScanIds, setBatchScanIds] = useState<string[]>([]);
  const [batchSettingsByScan, setBatchSettingsByScan] = useState<Record<string, BatchScanSettings>>({});
  const [batchResults, setBatchResults] = useState<SubmissionResult[]>([]);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Batch settings
  const [batchPhotoboothName, setBatchPhotoboothName] = useState('');
  const [batchLpFront, setBatchLpFront] = useState<LicensePlateOption>('no_plate');
  const [batchLpRear, setBatchLpRear] = useState<LicensePlateOption>('bracket_only');
  const [batchHighReflectance, setBatchHighReflectance] = useState(false);
  const [batchMetadataFree, setBatchMetadataFree] = useState(false);
  const [batchGenerationTier, setBatchGenerationTier] = useState<GenerationTierOption>('tier4');

  // Shared photobooth list
  const [photoboothNames, setPhotoboothNames] = useState<string[]>([]);

  // Backend defaults for license plate (used to decide when to send settings)
  const [lpDefaults, setLpDefaults] = useState<{ front: LicensePlateOption; rear: LicensePlateOption }>({ front: 'no_plate', rear: 'bracket_only' });
  // Backend default for high reflectance (used to decide when to send settings)
  const [hrDefault, setHrDefault] = useState(false);
  // Backend default for metadata free mode (used to decide when to send settings)
  const [mfDefault, setMfDefault] = useState(false);

  useEffect(() => {
    apiFetch('/api/settings/photobooths')
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setPhotoboothNames(
            Array.isArray(data) ? data.map((p: { name: string }) => p.name) : []
          );
        }
      })
      .catch((err) => log.error('Failed to fetch photobooths:', err));

    apiFetch('/api/settings/scan-defaults')
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          const front = (data?.license_plate?.front ?? 'no_plate') as LicensePlateOption;
          const rear = (data?.license_plate?.rear ?? 'bracket_only') as LicensePlateOption;
          setLpFront(front); setBatchLpFront(front);
          setLpRear(rear); setBatchLpRear(rear);
          setLpDefaults({ front, rear });
          const hr = Boolean(data?.high_reflectance ?? false);
          setHighReflectance(hr); setBatchHighReflectance(hr);
          setHrDefault(hr);
          const mf = Boolean(data?.metadata_free ?? false);
          setMetadataFree(mf); setBatchMetadataFree(mf);
          setMfDefault(mf);
          const tier = data?.generation_tier as GenerationTierOption;
          if (GENERATION_TIER_OPTIONS.some((option) => option.value === tier)) {
            setGenerationTier(tier);
            setBatchGenerationTier(tier);
          }
        }
      })
      .catch((err) => log.error('Failed to fetch scan defaults:', err));
  }, []);

  // Submit a single scan
  const submitSingleScan = async () => {
    const scanId = singleScanId.trim();
    if (!scanId) return;

    setSingleSubmitting(true);
    setSingleResult(null);
    const submittedAt = Date.now();

    try {
      const payload: Record<string, unknown> = {
        scan_id: scanId,
        source: 'manual',
      };
      if (photoboothName) payload.photobooth_name = photoboothName;
      const hasLpSettings = lpFront !== lpDefaults.front || lpRear !== lpDefaults.rear;
      const hasHrSettings = highReflectance !== hrDefault;
      const hasMfSettings = metadataFree !== mfDefault;
      const hasBedCabin = bedType !== '' || cabinType !== '';
      payload.settings = {
        generation_tier: generationTier,
        ...(hasLpSettings ? { license_plate: { front: lpFront, rear: lpRear } } : {}),
        ...(hasHrSettings ? { high_reflectance: highReflectance } : {}),
        ...(hasMfSettings ? { metadata_free: metadataFree } : {}),
        ...(hasBedCabin && bedType ? { bed_type: bedType } : {}),
        ...(hasBedCabin && cabinType ? { cabin_type: cabinType } : {}),
      };

      const response = await apiFetch('/scans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        log.info(`Single scan submitted: scan_id=${scanId}, job_id=${data.job_id}`);
        setSingleResult({
          scan_id: scanId,
          job_id: data.job_id,
          status: 'submitted',
        });
        setSingleScanId('');
      } else {
        const error = await response.text();
        log.error(`Single scan submission failed: scan_id=${scanId}, status=${response.status}`, error);
        setSingleResult({
          scan_id: scanId,
          status: 'error',
          error: error || 'Failed to submit scan',
        });
      }
    } catch (err) {
      log.error('Single scan submission network error:', err);
      const acceptedJob = await findAcceptedLiveJob(
        scanId,
        generationTier,
        submittedAt,
      );
      if (acceptedJob) {
        log.info(`Recovered accepted scan submission: scan_id=${scanId}, job_id=${acceptedJob.job_id}`);
        setSingleResult({
          scan_id: scanId,
          job_id: acceptedJob.job_id,
          status: 'submitted',
        });
        setSingleScanId('');
        return;
      }

      setSingleResult({
        scan_id: scanId,
        status: 'error',
        error: err instanceof Error ? err.message : 'Network error',
      });
    } finally {
      setSingleSubmitting(false);
    }
  };

  // Parse CSV file. The scan-id column is required; optional 'pickup_bed' and
  // 'pickup_cabin' columns are parsed into per-scan settings when a header row
  // is present. Files without a header fall back to first-column-only.
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBatchFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) return;

      const splitRow = (line: string) => line.split(',').map((part) => part.trim());
      const isValidScanId = (id: string) =>
        /^[a-zA-Z0-9-_]+$/.test(id) && id.length > 5 && !SCAN_ID_HEADERS.includes(id.toLowerCase());

      const lines = text.split(/[\r\n]+/).filter((line) => line.trim());
      if (lines.length === 0) {
        setBatchScanIds([]);
        setBatchSettingsByScan({});
        setBatchResults([]);
        return;
      }

      // Detect an (optional) header row so we can locate columns by name.
      const header = splitRow(lines[0]).map((h) => h.toLowerCase());
      let scanCol = header.findIndex((h) => SCAN_ID_HEADERS.includes(h));
      const bedCol = header.indexOf(BED_COLUMN);
      const cabinCol = header.indexOf(CABIN_COLUMN);
      const hasHeader = scanCol >= 0 || bedCol >= 0 || cabinCol >= 0;
      if (scanCol < 0) scanCol = 0;
      const dataLines = hasHeader ? lines.slice(1) : lines;

      const validIds: string[] = [];
      const settingsMap: Record<string, BatchScanSettings> = {};
      for (const line of dataLines) {
        const parts = splitRow(line);
        const id = parts[scanCol];
        if (!id || !isValidScanId(id)) continue;
        validIds.push(id);

        const scanSettings: BatchScanSettings = {};
        if (bedCol >= 0) {
          const bed = (parts[bedCol] || '').toLowerCase();
          if (BED_TOKENS.has(bed)) scanSettings.bed_type = bed;
        }
        if (cabinCol >= 0) {
          const cabin = (parts[cabinCol] || '').toLowerCase();
          if (CABIN_TOKENS.has(cabin)) scanSettings.cabin_type = cabin;
        }
        if (scanSettings.bed_type || scanSettings.cabin_type) {
          settingsMap[id] = scanSettings;
        }
      }

      setBatchScanIds(validIds);
      setBatchSettingsByScan(settingsMap);
      setBatchResults([]);
    };

    reader.readAsText(file);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Submit batch scans via batch API
  const submitBatchScans = async () => {
    if (batchScanIds.length === 0 || !batchFileName) return;

    setBatchSubmitting(true);
    setBatchProgress({ current: 0, total: batchScanIds.length });

    try {
      const batchPayload: Record<string, unknown> = {
        filename: batchFileName,
        scan_ids: batchScanIds,
      };
      if (batchPhotoboothName) batchPayload.photobooth = batchPhotoboothName;
      const hasBatchLpSettings = batchLpFront !== lpDefaults.front || batchLpRear !== lpDefaults.rear;
      const hasBatchHrSettings = batchHighReflectance !== hrDefault;
      const hasBatchMfSettings = batchMetadataFree !== mfDefault;
      batchPayload.settings = {
        generation_tier: batchGenerationTier,
        ...(hasBatchLpSettings ? { license_plate: { front: batchLpFront, rear: batchLpRear } } : {}),
        ...(hasBatchHrSettings ? { high_reflectance: batchHighReflectance } : {}),
        ...(hasBatchMfSettings ? { metadata_free: batchMetadataFree } : {}),
      };
      // Per-scan pickup bed/cabin parsed from the CSV columns.
      if (Object.keys(batchSettingsByScan).length > 0) {
        batchPayload.settings_by_scan = batchSettingsByScan;
      }

      const response = await apiFetch('/api/batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batchPayload),
      });

      if (response.ok) {
        const data = await response.json();
        log.info(`Batch submitted: batch_id=${data.batch_id}, scans=${batchScanIds.length}, filename=${batchFileName}`);
        // Mark all as submitted
        const results: SubmissionResult[] = batchScanIds.map(scanId => ({
          scan_id: scanId,
          job_id: data.batch_id,
          status: 'submitted' as const,
        }));
        setBatchResults(results);
        setBatchProgress({ current: batchScanIds.length, total: batchScanIds.length });
      } else {
        const error = await response.text();
        log.error(`Batch submission failed: status=${response.status}, scans=${batchScanIds.length}`, error);
        // Mark all as error
        const results: SubmissionResult[] = batchScanIds.map(scanId => ({
          scan_id: scanId,
          status: 'error' as const,
          error: error || 'Failed to submit batch',
        }));
        setBatchResults(results);
      }
    } catch (err) {
      log.error('Batch submission network error:', err);
      const errorMsg = err instanceof Error ? err.message : 'Network error';
      const results: SubmissionResult[] = batchScanIds.map(scanId => ({
        scan_id: scanId,
        status: 'error' as const,
        error: errorMsg,
      }));
      setBatchResults(results);
    } finally {
      setBatchSubmitting(false);
    }
  };

  // Clear batch
  const clearBatch = () => {
    setBatchFileName(null);
    setBatchScanIds([]);
    setBatchSettingsByScan({});
    setBatchResults([]);
    setBatchProgress({ current: 0, total: 0 });
  };

  const successCount = batchResults.filter(r => r.status === 'submitted').length;
  const errorCount = batchResults.filter(r => r.status === 'error').length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Single Scan Section */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Single Scan</h2>
            <p className="text-sm text-slate-500">Generate images for one scan ID</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="scanId" className="block text-sm font-medium text-slate-700 mb-2">
              Scan ID
            </label>
            <input
              id="scanId"
              type="text"
              value={singleScanId}
              onChange={(e) => setSingleScanId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitSingleScan()}
              placeholder="e.g., 57942aad-a26d-4b05-8c1b-7de5aa32326d"
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 font-mono text-sm transition-all duration-200"
            />
          </div>

          {/* Settings */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="photobooth" className="block text-xs font-medium text-slate-600 mb-1">Background</label>
              <select
                id="photobooth"
                value={photoboothName}
                onChange={(e) => setPhotoboothName(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                <option value="">Server default</option>
                {photoboothNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lpFront" className="block text-xs font-medium text-slate-600 mb-1">Plate (Front)</label>
              <select
                id="lpFront"
                value={lpFront}
                onChange={(e) => setLpFront(e.target.value as LicensePlateOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {LP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lpRear" className="block text-xs font-medium text-slate-600 mb-1">Plate (Rear)</label>
              <select
                id="lpRear"
                value={lpRear}
                onChange={(e) => setLpRear(e.target.value as LicensePlateOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {LP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="generationTier" className="block text-xs font-medium text-slate-600 mb-1">Generation tier</label>
              <select
                id="generationTier"
                value={generationTier}
                onChange={(e) => setGenerationTier(e.target.value as GenerationTierOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {GENERATION_TIER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* High reflectance toggle */}
          <label htmlFor="highReflectance" className="flex items-center gap-2 cursor-pointer select-none">
            <input
              id="highReflectance"
              type="checkbox"
              checked={highReflectance}
              onChange={(e) => setHighReflectance(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-xs font-medium text-slate-600">
              High reflectance (outdoor-style reflections)
            </span>
          </label>

          {/* Metadata free mode toggle */}
          <label htmlFor="metadataFree" className="flex items-center gap-2 cursor-pointer select-none">
            <input
              id="metadataFree"
              type="checkbox"
              checked={metadataFree}
              onChange={(e) => setMetadataFree(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-xs font-medium text-slate-600">
              Metadata free mode (rely on reference images only)
            </span>
          </label>

          {/* Pre-known pickup bed/cabin (optional; only for pickup trucks) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="bedType" className="block text-xs font-medium text-slate-600 mb-1">Pickup bed</label>
              <select
                id="bedType"
                value={bedType}
                onChange={(e) => setBedType(e.target.value as BedTypeOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {BED_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="cabinType" className="block text-xs font-medium text-slate-600 mb-1">Pickup cabin</label>
              <select
                id="cabinType"
                value={cabinType}
                onChange={(e) => setCabinType(e.target.value as CabinTypeOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {CABIN_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={submitSingleScan}
            disabled={!singleScanId.trim() || singleSubmitting}
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {singleSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate
              </>
            )}
          </button>

          {/* Single scan result */}
          {singleResult && (
            <div className={`p-4 rounded-lg ${
              singleResult.status === 'submitted'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}>
              {singleResult.status === 'submitted' ? (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Scan submitted successfully</p>
                    <p className="text-xs font-mono mt-1 text-slate-600">Job ID: {singleResult.job_id}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Failed to submit scan</p>
                    <p className="text-xs text-slate-600 mt-1">{singleResult.error}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Batch Upload Section */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Batch Upload</h2>
            <p className="text-sm text-slate-500">Generate images from a CSV file</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* File upload area */}
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all duration-200">
              <svg className="w-12 h-12 mx-auto text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-slate-700">
                Drop a CSV file here or <span className="text-indigo-600 font-medium">browse</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                One scan ID per line or comma-separated
              </p>
            </div>
          </div>

          {/* Scan IDs preview */}
          {batchScanIds.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">
                    <span className="text-slate-900 font-semibold">{batchScanIds.length}</span> scan IDs loaded
                    {Object.keys(batchSettingsByScan).length > 0 && (
                      <span className="text-slate-500">
                        {' '}· <span className="font-semibold">{Object.keys(batchSettingsByScan).length}</span> with bed/cabin
                      </span>
                    )}
                  </span>
                  {batchFileName && (
                    <span className="text-xs text-slate-400 font-mono">({batchFileName})</span>
                  )}
                </div>
                <button
                  onClick={clearBatch}
                  className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {batchScanIds.slice(0, 10).map((id, idx) => (
                  <div key={idx} className="text-xs font-mono text-slate-500 truncate">
                    {id}
                  </div>
                ))}
                {batchScanIds.length > 10 && (
                  <div className="text-xs text-slate-400">
                    ... and {batchScanIds.length - 10} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Batch settings */}
          {batchScanIds.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="batchPhotobooth" className="block text-xs font-medium text-slate-600 mb-1">Background</label>
                <select
                  id="batchPhotobooth"
                  value={batchPhotoboothName}
                  onChange={(e) => setBatchPhotoboothName(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  <option value="">Server default</option>
                  {photoboothNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="batchLpFront" className="block text-xs font-medium text-slate-600 mb-1">Plate (Front)</label>
                <select
                  id="batchLpFront"
                  value={batchLpFront}
                  onChange={(e) => setBatchLpFront(e.target.value as LicensePlateOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {LP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="batchLpRear" className="block text-xs font-medium text-slate-600 mb-1">Plate (Rear)</label>
                <select
                  id="batchLpRear"
                  value={batchLpRear}
                  onChange={(e) => setBatchLpRear(e.target.value as LicensePlateOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {LP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="batchGenerationTier" className="block text-xs font-medium text-slate-600 mb-1">Generation tier</label>
                <select
                  id="batchGenerationTier"
                  value={batchGenerationTier}
                  onChange={(e) => setBatchGenerationTier(e.target.value as GenerationTierOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {GENERATION_TIER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Batch high reflectance / metadata free toggles */}
          {batchScanIds.length > 0 && (
            <div className="space-y-3">
              <label htmlFor="batchHighReflectance" className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  id="batchHighReflectance"
                  type="checkbox"
                  checked={batchHighReflectance}
                  onChange={(e) => setBatchHighReflectance(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-xs font-medium text-slate-600">
                  High reflectance (outdoor-style reflections)
                </span>
              </label>

              <label htmlFor="batchMetadataFree" className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  id="batchMetadataFree"
                  type="checkbox"
                  checked={batchMetadataFree}
                  onChange={(e) => setBatchMetadataFree(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-xs font-medium text-slate-600">
                  Metadata free mode (rely on reference images only)
                </span>
              </label>
            </div>
          )}

          {/* Submit batch button */}
          <button
            onClick={submitBatchScans}
            disabled={batchScanIds.length === 0 || batchSubmitting}
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {batchSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting {batchProgress.current} / {batchProgress.total}...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Submit Batch ({batchScanIds.length} scans)
              </>
            )}
          </button>

          {/* Batch results summary */}
          {batchResults.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-900">Submission Results</span>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-green-600">{successCount} submitted</span>
                  {errorCount > 0 && <span className="text-red-600">{errorCount} failed</span>}
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-200 rounded-full h-2 mb-3 overflow-hidden">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
                  style={{ width: `${(successCount / batchResults.length) * 100}%` }}
                />
              </div>

              {/* Show errors if any */}
              {errorCount > 0 && (
                <div className="max-h-24 overflow-y-auto space-y-1">
                  {batchResults
                    .filter(r => r.status === 'error')
                    .slice(0, 5)
                    .map((result, idx) => (
                      <div key={idx} className="text-xs text-red-600 truncate">
                        {result.scan_id}: {result.error}
                      </div>
                    ))}
                </div>
              )}

              <Link
                to="/lightning/live"
                className="mt-4 block w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-center text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                View Progress in Live Scans
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
